/**
 * QuoteReadinessChecklist — scannable send gates (compact checklist).
 */

import React, { useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  ErrorOutline,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import {
  buildQuoteReadinessChecklist,
  getChecklistStatusIcon,
} from "../utils/quoteReadinessChecklist.js";

const StatusIcon = ({ status }) => {
  if (status === "ok") {
    return <CheckCircleOutline fontSize="small" color="success" />;
  }
  if (status === "warn") {
    return <ErrorOutline fontSize="small" color="warning" />;
  }
  if (status === "pending") {
    return <RadioButtonUnchecked fontSize="small" color="disabled" />;
  }
  return (
    <Typography variant="caption" color="text.secondary">
      {getChecklistStatusIcon(status)}
    </Typography>
  );
};

const QuoteReadinessChecklist = ({ quote, review = null, dense = false }) => {
  const items = useMemo(
    () => buildQuoteReadinessChecklist(quote, review),
    [quote, review]
  );

  if (!quote || items.length === 0) return null;

  return (
    <Box data-testid="quote-readiness-checklist">
      {!dense && (
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} mb={0.5}>
          Readiness checklist
        </Typography>
      )}
      <List dense disablePadding>
        {items.map((item) => (
          <ListItem key={item.id} disableGutters sx={{ py: 0.25 }}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <StatusIcon status={item.status} />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                variant: dense ? "caption" : "body2",
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default QuoteReadinessChecklist;
